import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Col,
  Container,
  Row,
  Card,
  CardBody,
  Input,
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
} from "reactstrap";

import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import BreadCrumb from "Components/Common/BreadCrumb";
import Loader from "Components/Common/Loader";
import { useFormik } from "formik";
import * as Yup from "yup";

import type { RootState } from "store";
import type { ThunkDispatch } from "redux-thunk";
import type { AnyAction } from "redux";
import ApplicationContainer from "./applicationContainer";
import ApplicationForm from "./applicationform";
import { handleValidDate, handleValidTime } from "helpers/common_helper";

import NewDeleteModal from "Components/Common/NewDeleteModal";
import { toast } from "react-toastify";

import {
  fetchApplicationsThunk,
  addApplicationThunk,
} from "slices/RBAC/thunk";

import { Link, Navigate, useNavigate } from "react-router-dom";

const selectRBACState = (state: RootState) => (state as any).rbac;

const rbacSelector = createSelector(selectRBACState, (rbac) => ({
  applications: rbac.applications,
  loading: rbac.loading,
}));

const ApplicationPage: React.FC = () => {
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();
  const { applications, loading } = useSelector(rbacSelector);

  const applicationList = applications || [];

  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedApplicationId, setSelectedApplicationId] = useState<number | null>(null);

  const loadApplicationList = useCallback(() => {
    return dispatch(fetchApplicationsThunk());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchApplicationsThunk());
  }, [dispatch]);

  const navigate = useNavigate();

  const handleDeleteClick = (id: number) => {
    setSelectedApplicationId(id);
    setDeleteModalOpen(true);
  };

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        applicationid: Yup.number().required("Application ID is required"),
        applicationname: Yup.string().trim().required("Application name is required"),
        applicationdescription: Yup.string().trim().required("Application description is required"),
      }),
    []
  );

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      applicationid: "",
      applicationname: "",
      applicationdescription: "",
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      // Close modal immediately
      setIsModalOpen(false);
      resetForm();

      try {
        const res = await dispatch(
          addApplicationThunk({
            applicationid: Number(values.applicationid) || 0,
            organisationid: 1,
            applicationname: values.applicationname,
            applicationdescription: values.applicationdescription,
          })
        );
        if (!res?.success) {
          toast.error(res?.message || "Failed to add application");
          return;
        }
        toast.success("Application added successfully!");
        await dispatch(fetchApplicationsThunk());
      } catch (err) {
        console.error("Error saving application:", err);
        toast.error("An error occurred. Please try again.");
      }
    },
  });

  const toggleModal = () => {
    if (!isModalOpen) {
      validation.resetForm();
    }
    setIsModalOpen(!isModalOpen);
  };

  const handleRowClick = useCallback((row: any) => {}, []);

  const filteredApplications = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return applicationList;
    return applicationList.filter((t: any) =>
      t.applicationname?.toLowerCase().includes(q) ||
      t.applicationdescription?.toLowerCase().includes(q)
    );
  }, [applicationList, searchQuery]);

  const columns = useMemo(
    () => [
      {
        header: "Application ID",
        accessorKey: "applicationid",
        cell: ({ row }: any) => {
          const id = row.original.applicationid;
          return `#ID${String(id).padStart(3, "0")}`;
        },
      },
      { header: "Application Name", accessorKey: "applicationname" },
      { header: "Description", accessorKey: "applicationdescription" },
      {
        header: "Created On",
        accessorKey: "createdon",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const value = cell.getValue();
          return value ? (
            <>
              {handleValidDate(value)},{" "}
              <small className="text-muted">{handleValidTime(value)}</small>
            </>
          ) : (
            <>N/A</>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: (cell: any) => {
          const value = cell.getValue();
          const lowerVal = value?.toString().toLowerCase();
          const statusColor =
            lowerVal === "active" ? "#159E3A" : "#A65F00";

          return (
            <span
              style={{
                display: "inline-block",
                padding: "4px 12px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: "500",
                color: statusColor,
                border: `1px solid ${statusColor}`,
                textTransform: "capitalize",
              }}
            >
              {value}
            </span>
          );
        },
      },
      { header: "Created By", accessorKey: "createdby" },
    ],
    [handleDeleteClick]
  );

  return (
    <div className="page-content">
      <Container fluid className="border pt-4 rounded-4 bg-light mb-3 ms-3 pe-4">
        <BreadCrumb title="Application" pageTitle={["RBAC", "Applications"]} />

        <Row className="align-items-center g-3 d-flex justify-content-between pb-4 pe-3">
          <Col md={3}>
            {/* <div className="position-relative">
              <Input
                type="text"
                className="form-control border ps-5 ms-2 rounded-3"
                value={searchQuery}
                placeholder="Search Permission..."
                onChange={(e) => setSearchQuery(e.target.value)}
              /> */}
              {/* <i
                className="ri-search-line position-absolute top-50 start-0 translate-middle-y text-muted ms-3"
                style={{ fontSize: "1.1rem" }}
              />
            </div> */}
          </Col>
          <Col md={9} className="text-end">
          {/* <Button color="primary" onClick={toggleModal}>
          + Add New
        </Button> */}
          </Col>
        </Row>

        <Row>
          <Col lg={12}>
            <Card className="border-0 shadow-none">
              <CardBody>
                {!loading ? (
                  filteredApplications.length > 0 ? (
                    <ApplicationContainer
                      columns={columns}
                      data={filteredApplications}
                      customPageSize={10000}
                      limit={10000}
                      setPage={() => {}}
                      onRowClick={handleRowClick}
                      tableClass="align-middle border-bottom rounded-pill"
                      theadClass="bg-primary text-white"
                      divClass="table-responsive table-card"
                    />
                  ) : (
                    <div className="text-center p-4">
                      No records found.
                    </div>
                  )
                ) : (
                  <Loader />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Add Modal */}
      <Modal isOpen={isModalOpen} toggle={toggleModal} centered size="lg">
        <ModalHeader toggle={toggleModal}>
          Add New Application
        </ModalHeader>
        <form onSubmit={validation.handleSubmit}>
          <ModalBody>
            <ApplicationForm 
              validation={validation}
              isEdit={false}
            />
          </ModalBody>
          <ModalFooter>
            <Button
              type="submit"
              color="primary"
            >
              Add
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      <NewDeleteModal
        show={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Application"
        message="Are you sure you want to delete this Application?"
        onConfirm={async () => {
          if (selectedApplicationId) {
            setDeleteModalOpen(false);
            setSelectedApplicationId(null);
            toast.error("Delete functionality not available");
          }
        }}
      />
    </div>
  );
};

export default ApplicationPage;

